// ChipModal — bottom-sheet for picking a single value from a list of
// options on a known axis (category, location, state, cut, tile,
// expires). Wraps the existing ModalSheet primitive so we get the
// keyboard-aware visualViewport handling, swipe-dismiss, and animation
// for free.
//
// Used by AxisChipRow when the user taps a chip. Each axis pre-builds
// its option list and passes it here. No search input — these are
// scrollable lists of curated options.
//
// Design philosophy:
//   - Search-driven pickers (canonical, brand) use RisingResults.
//   - Curated-list pickers (category, state, location, etc.) use
//     ChipModal because the option count is small enough that scroll-
//     and-tap is faster than search-then-tap.
//
// Replaces MCMPickerSheet which had its own bespoke fixed-positioning
// without keyboard awareness.

import ModalSheet from "../../../components/ModalSheet";
import { font, color, radius, space } from "../../../lib/mcmTokens";

/**
 * Props:
 *   open         — boolean. Caller controls.
 *   onClose      — () => void.
 *   kicker       — uppercase label shown in the header (e.g. "CATEGORY")
 *   title        — italic question (e.g. "Where does it live?")
 *   accent       — axis color hex (drives kicker tint + active row)
 *   options      — array of { id, label, emoji?, sub? }
 *   value        — currently-selected option id (rendered with checkmark)
 *   onPick       — (option) => void. Caller closes after.
 *   children     — optional extra UI rendered above the option list
 *                  (e.g. a "Skip" row or "Show all" toggle)
 */
export default function ChipModal({
  open,
  onClose,
  kicker,
  title,
  accent = "#b8a878",
  options = [],
  value = null,
  onPick,
  children,
}) {
  if (!open) return null;
  return (
    <ModalSheet onClose={onClose} label={kicker}>
      <div style={{ padding: "0 4px 8px" }}>
        {kicker && (
          <div style={{
            fontFamily: font.mono,
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: accent,
            marginBottom: 6,
          }}>
            {kicker}
          </div>
        )}
        {title && (
          <h2 style={{
            fontFamily: font.detail,
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: 22,
            lineHeight: 1.15,
            color: color.ink,
            margin: 0,
            marginBottom: 14,
          }}>
            {title}
          </h2>
        )}
        {children}
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          {options.map((opt) => {
            const active = opt.id === value;
            return (
              <li key={opt.id}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onPick?.(opt);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: "12px 14px",
                    background: active
                      ? `${accent}1f`
                      : (color.glassFillHeavy || "rgba(255,255,255,0.04)"),
                    border: `1px solid ${active ? accent : (color.hairline || "rgba(0,0,0,0.08)")}`,
                    borderRadius: radius.field || 12,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {opt.emoji && (
                    <div style={{ fontSize: 20, flexShrink: 0 }}>{opt.emoji}</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: font.detail,
                      fontStyle: "italic",
                      fontSize: 15,
                      lineHeight: 1.15,
                      color: color.ink,
                    }}>
                      {opt.label}
                    </div>
                    {opt.sub && (
                      <div style={{
                        fontFamily: font.sans,
                        fontSize: 11,
                        color: color.inkMuted || "#6b6357",
                        marginTop: 2,
                      }}>
                        {opt.sub}
                      </div>
                    )}
                  </div>
                  {active && (
                    <div style={{
                      fontFamily: font.mono,
                      fontSize: 14,
                      color: accent,
                      flexShrink: 0,
                    }}>✓</div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </ModalSheet>
  );
}
